import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { calendarsAPI } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Calendar, Plus, Users, CheckCircle2 } from 'lucide-react';
import { CalendarGrid, dayLabel, localISO } from './CalendarGrid';
import { CalendarItemRow } from './CalendarItemRow';
import { TeamMeetingDialog } from './TeamMeetingDialog';

const TYPE_CFG = {
  reunion: { icon: Users, dot: 'bg-blue-500', text: 'text-blue-600', label: 'Reunión' },
  tarea: { icon: CheckCircle2, dot: 'bg-emerald-500', text: 'text-emerald-600', label: 'Tarea', isTask: true },
};

const emptyForm = (fecha) => ({ id: null, titulo: '', fecha: fecha || localISO(new Date()), hora_inicio: '', hora_fin: '', meet_link: '', descripcion: '', invitados: [] });

export default function EventsPage() {
  const navigate = useNavigate();
  const [range, setRange] = useState(null);
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!range) return;
    if (!silent) setLoading(true);
    try {
      const data = await calendarsAPI.teamRange(range.from, range.to);
      setItems(data?.items || []);
    } catch (e) { console.error('Error loading team calendar:', e); }
    finally { if (!silent) setLoading(false); }
  }, [range]);

  useEffect(() => { load(); const id = setInterval(() => load(true), 20000); return () => clearInterval(id); }, [load]);
  useEffect(() => { calendarsAPI.teamMembers().then(setMembers).catch(() => setMembers([])); }, []);

  const byDate = useMemo(() => {
    const map = {};
    for (const it of items) { if (it.date) (map[it.date] = map[it.date] || []).push(it); }
    return map;
  }, [items]);

  const openAdd = (iso) => { setForm(emptyForm(iso)); setDialog(true); };
  const openEdit = (it) => {
    setForm({ id: it.id, titulo: it.title || '', fecha: it.date, hora_inicio: it.time || '', hora_fin: it.end_time || '', meet_link: it.meet_link || '', descripcion: it.descripcion || '', invitados: it.invitados || [] });
    setDialog(true);
  };
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { titulo: form.titulo.trim(), fecha: form.fecha, hora_inicio: form.hora_inicio || null, hora_fin: form.hora_fin || null, meet_link: form.meet_link || null, descripcion: form.descripcion || null, invitados: form.invitados };
      if (form.id) await calendarsAPI.updateTeamMeeting(form.id, payload); else await calendarsAPI.createTeamMeeting(payload);
      toast.success(form.id ? 'Reunión actualizada' : 'Reunión creada');
      setDialog(false);
      await load(true);
    } catch (e) { toast.error(e?.message || 'No se pudo guardar la reunión'); }
    setSaving(false);
  };
  const [deleting, setDeleting] = useState(false);
  const handleDelete = async (it) => {
    const title = it.title || it.titulo || 'Sin título';
    setDeleting(true);
    try {
      await calendarsAPI.deleteTeamMeeting(it.id);
      setDialog(false); setSelectedDay(null);
      await load(true);
      toast.success(`«${title}» eliminada`, {
        duration: 10000,
        action: { label: 'Deshacer', onClick: async () => { try { await calendarsAPI.restoreTeamMeeting(it.id); toast.success('Reunión restaurada'); await load(true); } catch (e) { toast.error(e?.message || 'No se pudo restaurar'); } } },
      });
    } catch (e) { toast.error(e?.message || 'No se pudo eliminar'); }
    setDeleting(false);
  };
  const handleOpen = (it) => {
    if (it.link) navigate(it.link);
    else if (it.can_edit) openEdit(it);
  };

  const renderList = (list, iso, showAdd = true) => (
    list.length === 0 ? (
      <div className="text-center py-10">
        <p className="text-sm text-muted-foreground">No tienes nada este día</p>
        {showAdd && <Button variant="outline" size="sm" className="mt-3" onClick={() => openAdd(iso)} data-testid="add-meeting-empty-btn"><Plus className="w-4 h-4 mr-1" /> Nueva reunión</Button>}
      </div>
    ) : list.map((it, idx) => (
      <CalendarItemRow key={`${it.source_kind}-${it.id}-${idx}`} it={it} cfg={TYPE_CFG[it.type] || TYPE_CFG.reunion}
        onOpen={(it.link || it.can_edit) ? handleOpen : undefined}
        onEdit={it.can_edit ? openEdit : undefined} onDelete={it.can_edit ? ((x) => window.confirm(`¿Eliminar "${x.title}"?`) && handleDelete(x)) : undefined}
        testId={`day-item-${it.type}-${it.id}`} />
    ))
  );

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="events-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2"><Calendar className="w-7 h-7 text-primary" /> Mi calendario</h1>
          <p className="text-muted-foreground mt-1">Tus reuniones (con enlace de Meet) y tus tareas con fecha. Solo tú ves este calendario.</p>
        </div>
        <Button onClick={() => openAdd()} data-testid="add-meeting-btn"><Plus className="w-4 h-4 mr-2" /> Nueva reunión</Button>
      </div>
      <Card>
        <CardContent className="pt-5">
          <CalendarGrid items={items} typeCfg={TYPE_CFG} fallbackType="reunion" loading={loading}
            onRangeChange={setRange}
            onDayClick={(iso) => setSelectedDay(iso)}
            onItemClick={handleOpen}
            onAddAt={openAdd}
            renderDayList={(list, iso) => renderList(list, iso)} />
        </CardContent>
      </Card>

      <Dialog open={!!selectedDay} onOpenChange={(o) => !o && setSelectedDay(null)}>
        <DialogContent className="max-w-md" data-testid="day-detail-dialog">
          <DialogHeader>
            <DialogTitle className="capitalize">{selectedDay ? dayLabel(selectedDay) : ''}</DialogTitle>
            <DialogDescription>Todo lo que tienes ese día.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">{selectedDay && renderList(byDate[selectedDay] || [], selectedDay, false)}</div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { openAdd(selectedDay); setSelectedDay(null); }} data-testid="add-meeting-this-day-btn"><Plus className="w-4 h-4 mr-2" /> Nueva reunión este día</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TeamMeetingDialog open={dialog} onOpenChange={setDialog} form={form} setForm={setForm} members={members} onSave={handleSave} saving={saving} onDelete={handleDelete} deleting={deleting} />
    </motion.div>
  );
}
